import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, ArrowDownLeft, Check, X, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatCard } from "@/components/Ui";
import { api, errMsg } from "@/lib/api";
import { money, fmtDateTime } from "@/lib/format";

const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);
const ACCEPT = {
  teller_handover: (id) => [`/teller/handovers/${id}/acknowledge`, {}],
  cashier_collection: (id, amt) => [`/cashier/shifts/${id}/receive`, { counted_cash: amt, notes: "Accepted from cash acknowledgements" }],
  buffer_topup: (id) => [`/station/buffer-topups/${id}/acknowledge`, {}],
  cash_transfer: (id) => [`/cash-transfers/${id}/accept`, {}],
};
const REJECT = {
  teller_handover: (id) => `/teller/handovers/${id}/reject`,
  cashier_collection: (id) => `/cashier/shifts/${id}/reject`,
  buffer_topup: (id) => `/station/buffer-topups/${id}/reject`,
  cash_transfer: (id) => `/cash-transfers/${id}/reject`,
};
const STATUS = { pending: "bg-amber-50 text-amber-700", accepted: "bg-emerald-50 text-emerald-700", rejected: "bg-rose-50 text-rose-700" };

function Row({ tx, side, onAct }) {
  return (
    <div className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`tx-row-${tx.id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{tx.label}</div>
        <div className="text-xs text-slate-500">
          {side === "payments" ? `To ${tx.to_name || "—"}` : `From ${tx.from_name || "—"}`} · {fmtDateTime(tx.created_at)}
          {tx.status === "accepted" && tx.acted_by ? ` · accepted by ${tx.acted_by} ${tx.acted_at ? fmtDateTime(tx.acted_at) : ""}` : ""}
          {tx.status === "rejected" && tx.reject_reason ? ` · rejected: ${tx.reject_reason}` : ""}
        </div>
      </div>
      <b className={side === "payments" ? "text-rose-600" : "text-emerald-600"}>{side === "payments" ? "-" : "+"} {money(tx.amount)}</b>
      <span className={`chip capitalize ${STATUS[tx.status]}`} data-testid={`tx-status-${tx.id}`}>{tx.status}</span>
      {side === "receipts" && tx.can_act && (
        <div className="flex gap-2">
          <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => onAct(tx, true)} data-testid={`tx-approve-${tx.id}`}><Check className="h-4 w-4" />Approve</Button>
          <Button size="sm" variant="outline" className="rounded-full border-rose-200 text-rose-600" onClick={() => onAct(tx, false)} data-testid={`tx-reject-${tx.id}`}><X className="h-4 w-4" />Reject</Button>
        </div>
      )}
    </div>
  );
}

function Side({ side, items, onAct }) {
  const pending = items.filter((t) => t.status === "pending");
  const rejected = items.filter((t) => t.status === "rejected");
  return (
    <div className="space-y-4">
      {pending.length > 0 && (
        <div className="card-soft border-amber-200 bg-amber-50/50 p-4" data-testid={`tx-pending-${side}`}>
          <div className="label-xs mb-2 text-amber-700">{side === "payments" ? "Handed over — waiting to be accepted" : "Waiting for your approval"} ({pending.length})</div>
          <div className="space-y-1">{pending.map((t) => <Row key={t.id} tx={t} side={side} onAct={onAct} />)}</div>
        </div>
      )}
      {rejected.length > 0 && (
        <div className="card-soft border-rose-200 bg-rose-50/50 p-4" data-testid={`tx-rejected-${side}`}>
          <div className="label-xs mb-2 text-rose-700">Rejected ({rejected.length})</div>
          <div className="space-y-1">{rejected.map((t) => <Row key={t.id} tx={t} side={side} onAct={onAct} />)}</div>
        </div>
      )}
      <div className="card-soft divide-y divide-slate-100">
        <div className="label-xs p-4">{side === "payments" ? "Accepted payments" : "Accepted receipts"}</div>
        {items.filter((t) => t.status === "accepted").map((t) => <Row key={t.id} tx={t} side={side} onAct={onAct} />)}
        {!items.filter((t) => t.status === "accepted").length && <p className="p-6 text-center text-sm text-slate-500">Nothing accepted in this date range.</p>}
      </div>
    </div>
  );
}

export default function TransactionLedger() {
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/cash/transactions", { params: { from_date: from, to_date: to } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [from, to]);
  useEffect(() => { load(); }, [load]);
  const act = async (tx, approve) => {
    try {
      if (approve) { const [url, body] = ACCEPT[tx.kind](tx.id, tx.amount); await api.post(url, body); toast.success("Cash accepted"); }
      else { const reason = window.prompt("Why are you rejecting this cash?"); if (!reason) return; await api.post(REJECT[tx.kind](tx.id), { reason }); toast.success("Rejected"); }
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-5" data-testid="transaction-ledger">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="font-display text-2xl font-extrabold tracking-tight">Cash acknowledgements</h2>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs font-semibold text-slate-600">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="tx-from-input" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="tx-to-input" /></div>
        </div>
      </div>
      {!data ? <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div> : (
        <>
          {data.cash_in_hand != null && <StatCard label="My cash in hand" value={money(data.cash_in_hand)} icon={Wallet} tint="bg-sky-50 text-sky-600" testid="tx-cash-in-hand" />}
          <Tabs defaultValue="payments">
            <TabsList className="rounded-xl">
              <TabsTrigger value="payments" data-testid="tx-payments-tab"><ArrowUpRight className="mr-1 h-4 w-4" />My payments</TabsTrigger>
              <TabsTrigger value="receipts" data-testid="tx-receipts-tab"><ArrowDownLeft className="mr-1 h-4 w-4" />My receipts</TabsTrigger>
            </TabsList>
            <TabsContent value="payments"><Side side="payments" items={data.payments} onAct={act} /></TabsContent>
            <TabsContent value="receipts"><Side side="receipts" items={data.receipts} onAct={act} /></TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
